import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ICONS } from '../../icons';
import { useTranslation } from 'react-i18next';
import { createAvailableQuest, updateAvailableQuest, deleteAvailableQuest, uploadQuestIcon, deleteQuestIcon } from '../../services/adminService';
import { exportQuestCompletionsReport } from '../../utils/adminReports';
import { MarkdownEditor, MarkdownDisplay, QuestLabel } from '../shared';
import AdminListItem from './AdminListItem';
import AdminEmptyState from './AdminEmptyState';
import AdminPageLayout from './AdminPageLayout';
import AdminSearchBar from './AdminSearchBar';

export default function AdminQuestManager({ availableQuests = [], loading }) {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuest, setSelectedQuest] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('en');
  const defaultProofRequirements = [
    {
      label: { en: 'Link to VOD', tr: 'VOD Linki', nl: 'Link naar VOD' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: true
    },
    {
      label: { en: 'Stream Summary (excel doc)', tr: 'Yayın Özeti (excel belgesi)', nl: 'Stream Samenvatting (excel doc)' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'excel',
      required: true
    },
    {
      label: { en: 'TwitchTracker link', tr: 'TwitchTracker linki', nl: 'TwitchTracker link' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: false
    }
  ];

  const [newQuest, setNewQuest] = useState({
    title: { en: '', tr: '', nl: '' },
    description: { en: '', tr: '', nl: '' },
    difficulty: 'medium',
    value: 100,
    timeEstimate: '',
    sideQuests: [],
    proofRequirements: [...defaultProofRequirements],
    icon: null,
    isActive: false,
    missionBriefUrl: '',
    pressKitUrl: ''
  });
  const [showSideQuestModal, setShowSideQuestModal] = useState(false);
  const [editingSideQuestIndex, setEditingSideQuestIndex] = useState(null);
  const [newSideQuest, setNewSideQuest] = useState({
    title: { en: '', tr: '', nl: '' },
    description: { en: '', tr: '', nl: '' },
    difficulty: 'medium',
    value: 50,
    timeEstimate: '',
    proofRequirements: []
  });
  const [showProofModal, setShowProofModal] = useState(false);
  const [editingProofIndex, setEditingProofIndex] = useState(null);
  const [newProofRequirement, setNewProofRequirement] = useState({
    label: { en: '', tr: '', nl: '' },
    description: { en: '', tr: '', nl: '' },
    inputType: 'link',
    required: true
  });
  const [showSideQuestProofModal, setShowSideQuestProofModal] = useState(false);
  const [editingSideQuestProofIndex, setEditingSideQuestProofIndex] = useState(null);
  const [newSideQuestProofRequirement, setNewSideQuestProofRequirement] = useState({
    label: { en: '', tr: '', nl: '' },
    description: { en: '', tr: '', nl: '' },
    inputType: 'link',
    required: true
  });
  const [isEditing, setIsEditing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Removed unused getQuestStats to resolve lint error
  const languages = [
    { code: 'en', name: 'English' },
    { code: 'nl', name: 'Dutch' }
  ];

  const filteredQuests = availableQuests.filter(quest => {
    const searchLower = searchTerm.toLowerCase();
    
    // Handle title (can be string or object with language keys)
    const titleText = typeof quest.title === 'string' 
      ? quest.title 
      : quest.title?.en || quest.title?.tr || quest.title?.nl || '';
    
    // Handle description (can be string or object with language keys)  
    const descriptionText = typeof quest.description === 'string'
      ? quest.description
      : quest.description?.en || quest.description?.tr || quest.description?.nl || '';
    
    return titleText.toLowerCase().includes(searchLower) ||
           descriptionText.toLowerCase().includes(searchLower);
  });

  const getCompletedTranslations = (textObj) => {
    return languages.filter(lang => textObj[lang.code] && textObj[lang.code].trim() !== '').length;
  };

  const isLanguageCompleted = (textObj, langCode) => {
    return textObj[langCode] && textObj[langCode].trim() !== '';
  };

  const handleCreateQuest = async () => {
    setCreating(true);
    try {
      await createAvailableQuest(newQuest);
      setShowCreateModal(false);
      setNewQuest({
        title: { en: '', tr: '', nl: '' },
        description: { en: '', tr: '', nl: '' },
        difficulty: 'medium',
        value: 100,
        timeEstimate: '',
        sideQuests: [],
        proofRequirements: [...defaultProofRequirements],
        icon: null,
        isActive: false,
        missionBriefUrl: '',
        pressKitUrl: ''
      });
      setSelectedLanguage('en');
      // Quest will be automatically updated via the parent component's subscription
    } catch (error) {
      console.error('Error creating quest:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateQuest = async () => {
    if (!selectedQuest) return;
    setCreating(true);
    try {
      await updateAvailableQuest(selectedQuest.id, newQuest);
      setShowCreateModal(false);
      setIsEditing(false);
      // Data will refresh from subscription
    } catch (error) {
      console.error('Error updating quest:', error);
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    setIsEditing(false);
    setNewQuest({
      title: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      difficulty: 'medium',
      value: 100,
      timeEstimate: '',
      sideQuests: [],
      proofRequirements: [...defaultProofRequirements],
      icon: null,
      isActive: false,
      missionBriefUrl: '',
      pressKitUrl: ''
    });
    setSelectedLanguage('en');
    setImagePreview(null);
    setShowCreateModal(true);
  };

  const openEditModal = (quest) => {
    setIsEditing(true);
    // Ensure all fields have defaults for backward compatibility
    const questWithDefaults = {
      ...quest,
      isActive: quest.isActive || false,
      missionBriefUrl: quest.missionBriefUrl || '',
      pressKitUrl: quest.pressKitUrl || '',
      sideQuests: (quest.sideQuests || []).map(sideQuest => ({
        ...sideQuest,
        proofRequirements: sideQuest.proofRequirements || []
      }))
    };
    setNewQuest(questWithDefaults);
    setSelectedQuest(quest);
    setImagePreview(quest.icon || null);
    setShowCreateModal(true);
  };

  const updateNewQuest = (field, value, language = null) => {
    setNewQuest(prev => {
      if (language) {
        return {
          ...prev,
          [field]: {
            ...prev[field],
            [language]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const updateNewSideQuest = (field, value, language = null) => {
    setNewSideQuest(prev => {
      if (language) {
        return {
          ...prev,
          [field]: {
            ...prev[field],
            [language]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleAddSideQuest = () => {
    setNewSideQuest({
      title: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      difficulty: 'medium',
      value: 50,
      timeEstimate: '',
      proofRequirements: []
    });
    setEditingSideQuestIndex(null);
    setShowSideQuestModal(true);
  };

  const handleEditSideQuest = (index) => {
    const sideQuest = newQuest.sideQuests[index];
    // Ensure proofRequirements exists for backward compatibility
    const sideQuestWithProofRequirements = {
      ...sideQuest,
      proofRequirements: sideQuest.proofRequirements || []
    };
    setNewSideQuest(sideQuestWithProofRequirements);
    setEditingSideQuestIndex(index);
    setShowSideQuestModal(true);
  };

  const handleSaveSideQuest = () => {
    setNewQuest(prev => {
      const updatedSideQuests = [...prev.sideQuests];
      if (editingSideQuestIndex !== null) {
        updatedSideQuests[editingSideQuestIndex] = newSideQuest;
      } else {
        updatedSideQuests.push(newSideQuest);
      }
      return {
        ...prev,
        sideQuests: updatedSideQuests
      };
    });
    setShowSideQuestModal(false);
    setNewSideQuest({
      title: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      difficulty: 'medium',
      value: 50,
      timeEstimate: '',
      proofRequirements: []
    });
    setEditingSideQuestIndex(null);
  };

  const handleDeleteSideQuest = (index) => {
    setNewQuest(prev => ({
      ...prev,
      sideQuests: prev.sideQuests.filter((_, i) => i !== index)
    }));
  };

  const updateNewProofRequirement = (field, value, language = null) => {
    setNewProofRequirement(prev => {
      if (language) {
        return {
          ...prev,
          [field]: {
            ...prev[field],
            [language]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleAddProofRequirement = () => {
    setNewProofRequirement({
      label: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: true
    });
    setEditingProofIndex(null);
    setShowProofModal(true);
  };

  const handleEditProofRequirement = (index) => {
    setNewProofRequirement(newQuest.proofRequirements[index]);
    setEditingProofIndex(index);
    setShowProofModal(true);
  };

  const handleSaveProofRequirement = () => {
    setNewQuest(prev => {
      const updatedProofRequirements = [...prev.proofRequirements];
      if (editingProofIndex !== null) {
        updatedProofRequirements[editingProofIndex] = newProofRequirement;
      } else {
        updatedProofRequirements.push(newProofRequirement);
      }
      return {
        ...prev,
        proofRequirements: updatedProofRequirements
      };
    });
    setShowProofModal(false);
    setNewProofRequirement({
      label: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: true
    });
    setEditingProofIndex(null);
  };

  const handleDeleteProofRequirement = (index) => {
    setNewQuest(prev => ({
      ...prev,
      proofRequirements: prev.proofRequirements.filter((_, i) => i !== index)
    }));
  };

  // Side Quest Proof Requirements Functions
  const updateNewSideQuestProofRequirement = (field, value, language = null) => {
    setNewSideQuestProofRequirement(prev => {
      if (language) {
        return {
          ...prev,
          [field]: {
            ...prev[field],
            [language]: value
          }
        };
      } else {
        return {
          ...prev,
          [field]: value
        };
      }
    });
  };

  const handleAddSideQuestProofRequirement = () => {
    setNewSideQuestProofRequirement({
      label: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: true
    });
    setEditingSideQuestProofIndex(null);
    setShowSideQuestProofModal(true);
  };

  const handleEditSideQuestProofRequirement = (index) => {
    const proofRequirements = newSideQuest.proofRequirements || [];
    setNewSideQuestProofRequirement(proofRequirements[index]);
    setEditingSideQuestProofIndex(index);
    setShowSideQuestProofModal(true);
  };

  const handleSaveSideQuestProofRequirement = () => {
    setNewSideQuest(prev => {
      const currentProofRequirements = prev.proofRequirements || [];
      const updatedProofRequirements = [...currentProofRequirements];
      if (editingSideQuestProofIndex !== null) {
        updatedProofRequirements[editingSideQuestProofIndex] = newSideQuestProofRequirement;
      } else {
        updatedProofRequirements.push(newSideQuestProofRequirement);
      }
      return {
        ...prev,
        proofRequirements: updatedProofRequirements
      };
    });
    setShowSideQuestProofModal(false);
    setNewSideQuestProofRequirement({
      label: { en: '', tr: '', nl: '' },
      description: { en: '', tr: '', nl: '' },
      inputType: 'link',
      required: true
    });
    setEditingSideQuestProofIndex(null);
  };

  const handleDeleteSideQuestProofRequirement = (index) => {
    setNewSideQuest(prev => ({
      ...prev,
      proofRequirements: (prev.proofRequirements || []).filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Upload to Firebase Storage
      const imageUrl = await uploadQuestIcon(file);
      setNewQuest(prev => ({
        ...prev,
        icon: imageUrl
      }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Error uploading image: ' + error.message);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = async () => {
    if (newQuest.icon) {
      try {
        await deleteQuestIcon(newQuest.icon);
      } catch (error) {
        console.error('Error deleting old image:', error);
      }
    }
    setNewQuest(prev => ({
      ...prev,
      icon: null
    }));
    setImagePreview(null);
  };

  const handleDeleteQuest = async () => {
    if (!selectedQuest) return;
    
    setDeleting(true);
    try {
      // Delete the quest icon if it exists
      if (selectedQuest.icon) {
        try {
          await deleteQuestIcon(selectedQuest.icon);
        } catch (error) {
          console.error('Error deleting quest icon:', error);
          // Continue with quest deletion even if icon deletion fails
        }
      }
      
      // Delete the quest
      await deleteAvailableQuest(selectedQuest.id);
      
      // Close modals and reset selection
      setShowDeleteModal(false);
      setSelectedQuest(null);
    } catch (error) {
      console.error('Error deleting quest:', error);
      alert('Error deleting quest: ' + error.message);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  const handleExportCompletions = async () => {
    setExporting(true);
    try {
      const count = await exportQuestCompletionsReport('approved');
      alert(`Successfully exported ${count} quest completions!`);
    } catch (error) {
      console.error('Error exporting quest completions:', error);
      alert('Error exporting quest completions: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleExportQuestData = async (quest) => {
    setExporting(true);
    try {
      const questTitle = typeof quest.title === 'string' ? quest.title : quest.title?.en || 'quest';
      const sanitizedTitle = questTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      const date = new Date().toISOString().split('T')[0];
      const filename = `${sanitizedTitle}_completions_${date}.xlsx`;
      
      const count = await exportQuestCompletionsReport('approved', filename, quest.id);
      alert(`Successfully exported ${count} completion(s) for "${questTitle}"!`);
    } catch (error) {
      console.error('Error exporting quest data:', error);
      alert('Error exporting quest data: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <FontAwesomeIcon icon={ICONS.SPINNER} spin />
        <p>{t('admin.loadingQuests')}</p>
      </div>
    );
  }

  return (
    <AdminPageLayout
      title={t('admin.questManagement')}
      className="admin-quest-manager"
      headerActions={
        <AdminSearchBar
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('admin.searchQuests')}
          countBadge={
            <span className="admin-count-badge">
              {filteredQuests.length} {t('common.quest', { count: filteredQuests.length })}
            </span>
          }
          actions={
            <button
              className="create-btn"
              onClick={openCreateModal}
            >
              <FontAwesomeIcon icon={ICONS.PLUS} />
              {t('admin.createQuest')}
            </button>
          }
        />
      }
      listContent={
        filteredQuests.map(quest => {
            const totalXp = (quest.value || 0) + (quest.sideQuests || []).reduce((acc, sq) => acc + (sq.value || 0), 0);
            const questTitle = typeof quest.title === 'string' ? quest.title : quest.title?.en || 'Untitled';
            const questDesc = typeof quest.description === 'string' ? quest.description : quest.description?.en || 'No description';
            return (
              <AdminListItem
                key={quest.id}
                title={questTitle}
                image={quest.icon || null}
                subtitle={questDesc}
                selected={selectedQuest?.id === quest.id}
                onClick={() => setSelectedQuest(quest)}
                badges={[
                  <QuestLabel key="diff" type="difficulty" value={quest.difficulty} size="small" />,
                  !quest.isActive && <QuestLabel key="status" type="status" value="failed" text={t('admin.status.inactive')} size="small" />
                ].filter(Boolean)}
                meta={[
                  quest.sideQuests?.length > 0 && { icon: ICONS.LIST, text: `${quest.sideQuests.length} side quest${quest.sideQuests.length !== 1 ? 's' : ''}` },
                  quest.proofRequirements?.length > 0 && { icon: ICONS.CLIPBOARD_LIST, text: `${quest.proofRequirements.length} proof req${quest.proofRequirements.length !== 1 ? 's' : ''}` },
                  { icon: ICONS.COINS, text: `${totalXp} XP` },
                  quest.timeEstimate && { icon: ICONS.CLOCK, text: quest.timeEstimate },
                ].filter(Boolean)}
              />
            )
          })
      }
      detailContent={
        selectedQuest ? (
          <div className="details-content">
              {/* Header */}
              <div className="admin-detail-header">
                  <div className="details-title">
                    {selectedQuest.icon && (
                      <img src={selectedQuest.icon} alt="" className="details-icon" />
                    )}
                    <div className="details-title-text">
                      <h3>{typeof selectedQuest.title === 'string' ? selectedQuest.title : selectedQuest.title?.en || 'Untitled'}</h3>
                      <div className="details-meta">
                        <QuestLabel
                          type="status"
                          value={selectedQuest.isActive ? 'active' : 'failed'}
                          text={selectedQuest.isActive ? 'Active' : 'Inactive'}
                          size="small"
                        />
                        <QuestLabel type="difficulty" value={selectedQuest.difficulty} size="small" />
                        <span className="meta-item">
                          <FontAwesomeIcon icon={ICONS.STAR} />
                          {(selectedQuest.value || 0) + (selectedQuest.sideQuests || []).reduce((acc, sq) => acc + (sq.value || 0), 0)} XP
                        </span>
                        {selectedQuest.timeEstimate && selectedQuest.timeEstimate !== 'N/A' && (
                          <span className="meta-item">
                            <FontAwesomeIcon icon={ICONS.CLOCK} />
                            {selectedQuest.timeEstimate}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="admin-detail-actions">
                    <button className="btn-icon has-tooltip" onClick={() => openEditModal(selectedQuest)} data-tooltip="Edit Quest">
                      <FontAwesomeIcon icon={ICONS.EDIT} />
                    </button>
                    <button
                      className="btn-icon has-tooltip"
                      onClick={() => handleExportQuestData(selectedQuest)}
                      disabled={exporting}
                      data-tooltip="Export Data"
                    >
                      <FontAwesomeIcon icon={exporting ? ICONS.SPINNER : ICONS.DOWNLOAD} spin={exporting} />
                    </button>
                    <button
                      className={`btn-icon has-tooltip ${selectedQuest.isActive ? '' : 'success'}`}
                      onClick={async () => {
                        try {
                          await updateAvailableQuest(selectedQuest.id, {
                            ...selectedQuest,
                            isActive: !selectedQuest.isActive
                          });
                          setSelectedQuest(prev => ({ ...prev, isActive: !prev.isActive }));
                        } catch (error) {
                          console.error('Error toggling quest status:', error);
                        }
                      }}
                      data-tooltip={selectedQuest.isActive ? 'Deactivate' : 'Activate'}
                    >
                      <FontAwesomeIcon icon={selectedQuest.isActive ? ICONS.EYE_SLASH : ICONS.EYE} />
                    </button>
                    <button className="btn-icon danger has-tooltip" onClick={openDeleteModal} data-tooltip="Delete">
                      <FontAwesomeIcon icon={ICONS.TRASH} />
                    </button>
                  </div>
                </div>

                {/* Stats Bar */}
                <div className="details-stats">
                  <div className="stat-item">
                    <span className="stat-value">{selectedQuest.sideQuests?.length || 0}</span>
                    <span className="stat-label">Side Quests</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{selectedQuest.proofRequirements?.length || 0}</span>
                    <span className="stat-label">Proof Reqs</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">{selectedQuest.createdAt ? new Date(selectedQuest.createdAt).toLocaleDateString() : '—'}</span>
                    <span className="stat-label">Created</span>
                  </div>
                </div>

                {/* Description */}
                <div className="admin-detail-section">
                  <h4>{t('admin.form.description')}</h4>
                  <div className="description-content">
                    <MarkdownDisplay content={typeof selectedQuest.description === 'string' ? selectedQuest.description : selectedQuest.description?.en || 'No description'} />
                  </div>
                </div>

                {selectedQuest.sideQuests && selectedQuest.sideQuests.length > 0 && (
                  <div className="admin-detail-section">
                    <h4>Side Quests ({selectedQuest.sideQuests.length})</h4>
                    <div className="list-items">
                      {selectedQuest.sideQuests.map((sideQuest, index) => (
                        <div key={index} className="list-item">
                          <div className="list-item-header">
                            <span className="list-item-title">
                              {typeof sideQuest.title === 'string' ? sideQuest.title : sideQuest.title?.en || 'Untitled'}
                            </span>
                            <div className="list-item-badges">
                              <QuestLabel type="difficulty" value={sideQuest.difficulty} size="small" />
                              <QuestLabel type="xp" value={sideQuest.value} size="small" />
                            </div>
                          </div>
                          <p className="list-item-desc">
                            {typeof sideQuest.description === 'string' ? sideQuest.description : sideQuest.description?.en || 'No description'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedQuest.proofRequirements && selectedQuest.proofRequirements.length > 0 && (
                  <div className="admin-detail-section">
                    <h4>Proof Requirements ({selectedQuest.proofRequirements.length})</h4>
                    <div className="list-items">
                      {selectedQuest.proofRequirements.map((proof, index) => (
                        <div key={index} className="list-item compact">
                          <span className="list-item-title">
                            {typeof proof.label === 'string' ? proof.label : proof.label?.en || 'Untitled'}
                          </span>
                          <div className="list-item-badges">
                            <span className={`type-badge ${proof.inputType}`}>{proof.inputType?.toUpperCase() || 'LINK'}</span>
                            {proof.required && <span className="required-badge">Required</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
          </div>
        ) : (
          <AdminEmptyState
            icon={ICONS.TARGET}
            description={t('admin.selectQuest')}
          />
        )
      }
    >
      {/* Create Quest Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal create-modal">
            <div className="modal-header">
              <h3>{isEditing ? 'Edit Quest' : t('admin.createNewQuest')}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowCreateModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <form className="create-form">
                <div className="form-grid">
                  {/* General Section - Title, Icon, Settings */}
                  <div className="form-section">
                    <h4 className="general">General</h4>

                    <div className="general-section-grid">
                      {/* Left: Icon */}
                      <div className="icon-upload-compact">
                        <div className="icon-preview-container">
                          {imagePreview || newQuest.icon ? (
                            <div className="icon-preview">
                              <img
                                src={imagePreview || newQuest.icon}
                                alt="Quest icon preview"
                              />
                              <button
                                type="button"
                                className="remove-icon-btn"
                                onClick={handleRemoveImage}
                                title="Remove icon"
                              >
                                <FontAwesomeIcon icon={ICONS.TIMES} />
                              </button>
                            </div>
                          ) : (
                            <div className="no-icon-placeholder">
                              <FontAwesomeIcon icon={ICONS.IMAGE} />
                            </div>
                          )}
                        </div>
                        <input
                          type="file"
                          id="quest-icon-upload"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          disabled={uploadingImage}
                        />
                        <label
                          htmlFor="quest-icon-upload"
                          className={`upload-icon-btn compact ${uploadingImage ? 'uploading' : ''}`}
                        >
                          {uploadingImage ? (
                            <>
                              <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <FontAwesomeIcon icon={ICONS.UPLOAD} />
                              {newQuest.icon ? 'Change' : 'Upload'}
                            </>
                          )}
                        </label>
                      </div>

                      {/* Right: Title and Settings */}
                      <div className="general-fields">
                        {/* Title */}
                        <div className="form-group">
                          <div className="label-with-language">
                            <label>
                              <FontAwesomeIcon icon={ICONS.EDIT} className="label-icon" />
                              {t('admin.form.title')}
                            </label>
                            <div className="language-controls compact">
                              <select
                                className="language-select"
                                value={selectedLanguage}
                                onChange={(e) => setSelectedLanguage(e.target.value)}
                              >
                                {languages.map(lang => (
                                  <option key={lang.code} value={lang.code}>
                                    {isLanguageCompleted(newQuest.title, lang.code) ? '✓ ' : ''}{lang.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                          <input
                            type="text"
                            value={newQuest.title[selectedLanguage]}
                            onChange={(e) => updateNewQuest('title', e.target.value, selectedLanguage)}
                            placeholder={`Enter title in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                          />
                        </div>

                        {/* Settings Row */}
                        <div className="settings-row compact">
                          <div className="form-group">
                            <label>
                              <FontAwesomeIcon icon={ICONS.CHART_BAR} className="label-icon" />
                              {t('admin.form.difficulty')}
                            </label>
                            <select
                              value={newQuest.difficulty}
                              onChange={(e) => updateNewQuest('difficulty', e.target.value)}
                            >
                              <option value="easy">{t('quests.difficulty.easy')}</option>
                              <option value="medium">{t('quests.difficulty.medium')}</option>
                              <option value="hard">{t('quests.difficulty.hard')}</option>
                            </select>
                          </div>

                          <div className="form-group">
                            <label>
                              <FontAwesomeIcon icon={ICONS.STAR} className="label-icon" />
                              {t('admin.form.xpValue')}
                            </label>
                            <input
                              type="number"
                              value={newQuest.value}
                              onChange={(e) => updateNewQuest('value', parseInt(e.target.value) || 0)}
                              min="0"
                              step="10"
                              placeholder="100"
                            />
                          </div>

                          <div className="form-group">
                            <label>
                              <FontAwesomeIcon icon={ICONS.CLOCK} className="label-icon" />
                              {t('admin.form.timeEstimate')}
                            </label>
                            <input
                              type="text"
                              value={newQuest.timeEstimate}
                              onChange={(e) => updateNewQuest('timeEstimate', e.target.value)}
                              placeholder="e.g., 30 min"
                            />
                          </div>

                          <div className="form-group">
                            <label>
                              <FontAwesomeIcon icon={ICONS.CHECK_CIRCLE} className="label-icon" />
                              Status
                            </label>
                            <button
                              type="button"
                              className={`status-toggle ${newQuest.isActive ? 'active' : 'inactive'}`}
                              onClick={() => updateNewQuest('isActive', !newQuest.isActive)}
                            >
                              <FontAwesomeIcon icon={newQuest.isActive ? ICONS.CHECK : ICONS.TIMES} />
                              {newQuest.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Description Section */}
                  <div className="form-section">
                    <h4 className="description">{t('admin.form.description')}</h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newQuest.description, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newQuest.description)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <MarkdownEditor
                        value={newQuest.description[selectedLanguage]}
                        onChange={(value) => updateNewQuest('description', value, selectedLanguage)}
                        placeholder={`Enter description in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                        rows={4}
                      />
                    </div>
                  </div>

                  {/* External Links Section */}
                  <div className="form-section">
                    <h4 className="external-links">External Links</h4>
                    <div className="form-hint">
                      Optional links that will appear as buttons on the quest page.
                    </div>
                    <div className="settings-row">
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} className="label-icon" />
                          Mission Brief URL
                        </label>
                        <input
                          type="url"
                          value={newQuest.missionBriefUrl || ''}
                          onChange={(e) => updateNewQuest('missionBriefUrl', e.target.value)}
                          placeholder="https://example.com/mission-brief"
                        />
                      </div>
                      <div className="form-group">
                        <label>
                          <FontAwesomeIcon icon={ICONS.DOWNLOAD} className="label-icon" />
                          Press Kit URL
                        </label>
                        <input
                          type="url"
                          value={newQuest.pressKitUrl || ''}
                          onChange={(e) => updateNewQuest('pressKitUrl', e.target.value)}
                          placeholder="https://example.com/press-kit"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Side Quests Section */}
                  <div className="form-section">
                    <h4 className="side-quests">Side Quests</h4>
                    <div className="side-quests-header">
                      <div className="form-hint">
                        Optional sub-tasks that can enhance the main quest experience and provide additional XP rewards.
                      </div>
                      <button 
                        type="button"
                        className="add-side-quest-btn"
                        onClick={handleAddSideQuest}
                      >
                        <FontAwesomeIcon icon={ICONS.PLUS} />
                        Add Side Quest
                      </button>
                    </div>
                    
                    {newQuest.sideQuests.length > 0 ? (
                      <div className="side-quests-list">
                        {newQuest.sideQuests.map((sideQuest, index) => (
                          <div key={index} className="side-quest-item-edit">
                            <div className="side-quest-summary">
                              <h5>{sideQuest.title.en || 'Untitled'}</h5>
                              <div className="side-quest-actions">
                                <button 
                                  type="button"
                                  className="edit-btn"
                                  onClick={() => handleEditSideQuest(index)}
                                  title="Edit side quest"
                                >
                                  <FontAwesomeIcon icon={ICONS.EDIT} />
                                </button>
                                <button 
                                  type="button"
                                  className="delete-btn"
                                  onClick={() => handleDeleteSideQuest(index)}
                                  title="Delete side quest"
                                >
                                  <FontAwesomeIcon icon={ICONS.TRASH} />
                                </button>
                              </div>
                            </div>
                            <div className="side-quest-meta">
                              <QuestLabel type="difficulty" value={sideQuest.difficulty} size="small" />
                              <QuestLabel type="xp" value={sideQuest.value} size="small" />
                              {sideQuest.timeEstimate && (
                                <QuestLabel type="time" value={sideQuest.timeEstimate} size="small" />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="no-side-quests">
                        <p>No side quests added yet. Side quests are optional mini-challenges that can enhance the main quest experience.</p>
                      </div>
                    )}
                  </div>

                  {/* Proof Requirements Section */}
                  <div className="form-section proof-requirements-section">
                    <div className="section-header">
                      <h4 className="section-title">
                        <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                        Proof Requirements
                      </h4>
                      <button 
                        type="button"
                        className="add-requirement-btn"
                        onClick={handleAddProofRequirement}
                      >
                        <FontAwesomeIcon icon={ICONS.PLUS} />
                        Add Proof Requirement
                      </button>
                    </div>
                    
                    <div className="section-description">
                      <p>Specify what proof users need to submit to complete this quest. This helps verify quest completion and ensures quality submissions.</p>
                    </div>
                    
                    {newQuest.proofRequirements.length > 0 ? (
                      <div className="proof-requirements-grid">
                        {newQuest.proofRequirements.map((proof, index) => (
                          <div key={index} className="proof-requirement-card">
                            <div className="proof-card-header">
                              <div className="proof-card-icon">
                                <FontAwesomeIcon icon={
                                  proof.inputType === 'image' ? ICONS.IMAGE :
                                  proof.inputType === 'pdf' ? ICONS.FILE_PDF :
                                  proof.inputType === 'excel' ? ICONS.FILE_EXCEL :
                                  ICONS.LINK
                                } />
                              </div>
                              <div className="proof-card-title">
                                <h5>{proof.label.en || 'Untitled'}</h5>
                                <div className="proof-card-badges">
                                  <span className={`input-type-mini ${proof.inputType}`}>
                                    {proof.inputType?.toUpperCase() || 'LINK'}
                                  </span>
                                  {proof.required && (
                                    <span className="required-mini">Required</span>
                                  )}
                                </div>
                              </div>
                              <div className="proof-card-actions">
                                <button 
                                  type="button"
                                  className="action-btn edit"
                                  onClick={() => handleEditProofRequirement(index)}
                                  title="Edit proof requirement"
                                >
                                  <FontAwesomeIcon icon={ICONS.EDIT} />
                                </button>
                                <button 
                                  type="button"
                                  className="action-btn delete"
                                  onClick={() => handleDeleteProofRequirement(index)}
                                  title="Delete proof requirement"
                                >
                                  <FontAwesomeIcon icon={ICONS.TRASH} />
                                </button>
                              </div>
                            </div>
                            
                            {proof.description?.en && (
                              <div className="proof-card-description">
                                <p>{proof.description.en}</p>
                              </div>
                            )}
                            
                            <div className="proof-card-footer">
                              <div className="input-type-info">
                                <span className="input-type-label">Input Type:</span>
                                <span className="input-type-format">
                                  {proof.inputType === 'image' ? 'PNG, JPG, JPEG, GIF' :
                                   proof.inputType === 'pdf' ? 'PDF documents' :
                                   proof.inputType === 'excel' ? 'CSV, XLS, XLSX files' :
                                   'URL/Web link'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state-proof">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                        </div>
                        <h5>No Proof Requirements</h5>
                        <p>Add proof requirements to help verify quest completion and ensure quality submissions from users.</p>
                        <button 
                          type="button"
                          className="empty-state-btn"
                          onClick={handleAddProofRequirement}
                        >
                          <FontAwesomeIcon icon={ICONS.PLUS} />
                          Add Your First Proof Requirement
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-actions">
              <button 
                className="create-btn"
                onClick={isEditing ? handleUpdateQuest : handleCreateQuest}
                disabled={creating || !newQuest.title.en || !newQuest.description.en}
              >
                {creating ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    {isEditing ? 'Updating...' : t('admin.creating')}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={isEditing ? ICONS.SAVE : ICONS.PLUS} />
                    {isEditing ? 'Update Quest' : t('admin.createQuest')}
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
              >
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Side Quest Modal */}
      {showSideQuestModal && (
        <div className="modal-overlay">
          <div className="modal side-quest-modal">
            <div className="modal-header">
              <h3>{editingSideQuestIndex !== null ? 'Edit Side Quest' : 'Add Side Quest'}</h3>
              <button 
                className="close-btn"
                onClick={() => setShowSideQuestModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <form className="create-form">
                <div className="form-grid">
                  {/* Side Quest Title */}
                  <div className="form-section">
                    <h4>Side Quest Title</h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newSideQuest.title, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newSideQuest.title)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={newSideQuest.title[selectedLanguage]}
                        onChange={(e) => updateNewSideQuest('title', e.target.value, selectedLanguage)}
                        placeholder={`Enter title in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                      />
                    </div>
                  </div>

                  {/* Side Quest Description */}
                  <div className="form-section">
                    <h4 className="description">Side Quest Description</h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newSideQuest.description, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newSideQuest.description)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <textarea
                        value={newSideQuest.description[selectedLanguage]}
                        onChange={(e) => updateNewSideQuest('description', e.target.value, selectedLanguage)}
                        placeholder={`Enter description in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                        rows="3"
                      />
                    </div>
                  </div>

                  {/* Side Quest Settings */}
                  <div className="form-section">
                    <h4 className="settings">Side Quest Settings</h4>
                    <div className="settings-row">
                      <div className="form-group">
                        <label>Difficulty</label>
                        <select
                          value={newSideQuest.difficulty}
                          onChange={(e) => updateNewSideQuest('difficulty', e.target.value)}
                        >
                          <option value="easy">{t('quests.difficulty.easy')}</option>
                          <option value="medium">{t('quests.difficulty.medium')}</option>
                          <option value="hard">{t('quests.difficulty.hard')}</option>
                        </select>
                      </div>

                      <div className="form-group">
                        <label>XP Value</label>
                        <input
                          type="number"
                          value={newSideQuest.value}
                          onChange={(e) => updateNewSideQuest('value', parseInt(e.target.value) || 0)}
                          min="0"
                          step="10"
                          placeholder="50"
                        />
                      </div>

                      <div className="form-group">
                        <label>Time Estimate</label>
                        <input
                          type="text"
                          value={newSideQuest.timeEstimate}
                          onChange={(e) => updateNewSideQuest('timeEstimate', e.target.value)}
                          placeholder="e.g., 15 minutes"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Side Quest Proof Requirements Section */}
                  <div className="form-section proof-requirements-section">
                    <div className="section-header">
                      <h4 className="section-title">
                        <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                        Proof Requirements
                      </h4>
                      <button 
                        type="button"
                        className="add-requirement-btn"
                        onClick={handleAddSideQuestProofRequirement}
                      >
                        <FontAwesomeIcon icon={ICONS.PLUS} />
                        Add Proof Requirement
                      </button>
                    </div>
                    
                    <div className="section-description">
                      <p>Specify what proof users need to submit to complete this side quest.</p>
                    </div>
                    
                    {(newSideQuest.proofRequirements || []).length > 0 ? (
                      <div className="proof-requirements-grid">
                        {(newSideQuest.proofRequirements || []).map((proof, index) => (
                          <div key={index} className="proof-requirement-card">
                            <div className="proof-card-header">
                              <div className="proof-card-icon">
                                <FontAwesomeIcon icon={
                                  proof.inputType === 'image' ? ICONS.IMAGE :
                                  proof.inputType === 'pdf' ? ICONS.FILE_PDF :
                                  proof.inputType === 'excel' ? ICONS.FILE_EXCEL :
                                  ICONS.LINK
                                } />
                              </div>
                              <div className="proof-card-title">
                                <h5>{proof.label.en || 'Untitled'}</h5>
                                <div className="proof-card-badges">
                                  <span className={`input-type-mini ${proof.inputType}`}>
                                    {proof.inputType?.toUpperCase() || 'LINK'}
                                  </span>
                                  {proof.required && (
                                    <span className="required-mini">Required</span>
                                  )}
                                </div>
                              </div>
                              <div className="proof-card-actions">
                                <button 
                                  type="button"
                                  className="action-btn edit"
                                  onClick={() => handleEditSideQuestProofRequirement(index)}
                                  title="Edit proof requirement"
                                >
                                  <FontAwesomeIcon icon={ICONS.EDIT} />
                                </button>
                                <button 
                                  type="button"
                                  className="action-btn delete"
                                  onClick={() => handleDeleteSideQuestProofRequirement(index)}
                                  title="Delete proof requirement"
                                >
                                  <FontAwesomeIcon icon={ICONS.TRASH} />
                                </button>
                              </div>
                            </div>
                            
                            {proof.description?.en && (
                              <div className="proof-card-description">
                                <p>{proof.description.en}</p>
                              </div>
                            )}
                            
                            <div className="proof-card-footer">
                              <div className="input-type-info">
                                <span className="input-type-label">Input Type:</span>
                                <span className="input-type-format">
                                  {proof.inputType === 'image' ? 'PNG, JPG, JPEG, GIF' :
                                   proof.inputType === 'pdf' ? 'PDF documents' :
                                   proof.inputType === 'excel' ? 'CSV, XLS, XLSX files' :
                                   'URL/Web link'}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state-proof">
                        <div className="empty-state-icon">
                          <FontAwesomeIcon icon={ICONS.CLIPBOARD_LIST} />
                        </div>
                        <h5>No Proof Requirements</h5>
                        <p>Add proof requirements to help verify side quest completion.</p>
                        <button 
                          type="button"
                          className="empty-state-btn"
                          onClick={handleAddSideQuestProofRequirement}
                        >
                          <FontAwesomeIcon icon={ICONS.PLUS} />
                          Add Your First Proof Requirement
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-actions">
              <button 
                className="create-btn"
                onClick={handleSaveSideQuest}
                disabled={!newSideQuest.title.en || !newSideQuest.description.en}
              >
                <FontAwesomeIcon icon={ICONS.CHECK} />
                {editingSideQuestIndex !== null ? 'Update' : 'Add'} Side Quest
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowSideQuestModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Proof Requirement Modal */}
      {showProofModal && (
        <div className="modal-overlay">
          <div className="modal proof-requirement-modal large-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={ICONS.PLUS} />
                {editingProofIndex !== null ? 'Edit Proof Requirement' : 'Add Proof Requirement'}
              </h3>
              <button 
                className="close-btn"
                onClick={() => setShowProofModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <form className="create-form">
                <div className="form-grid">
                  {/* Proof Label Section */}
                  <div className="form-section">
                    <h4>
                      <FontAwesomeIcon icon={ICONS.TAG} />
                      Proof Label
                    </h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newProofRequirement.label, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newProofRequirement.label)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={newProofRequirement.label[selectedLanguage]}
                        onChange={(e) => updateNewProofRequirement('label', e.target.value, selectedLanguage)}
                        placeholder={`Enter label in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                      />
                    </div>
                  </div>

                  {/* Description Section */}
                  <div className="form-section">
                    <h4 className="description">
                      <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                      Description
                    </h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newProofRequirement.description, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newProofRequirement.description)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <textarea
                        value={newProofRequirement.description[selectedLanguage]}
                        onChange={(e) => updateNewProofRequirement('description', e.target.value, selectedLanguage)}
                        placeholder={`Enter description in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                        rows="3"
                      />
                    </div>
                  </div>

                  {/* Input Type Section */}
                  <div className="form-section">
                    <h4 className="settings">
                      <FontAwesomeIcon icon={ICONS.FILE_ALT} />
                      Input Type
                    </h4>
                    <div className="input-type-grid">
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="inputType"
                            value="image"
                            checked={newProofRequirement.inputType === 'image'}
                            onChange={(e) => updateNewProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.IMAGE} className="input-type-icon" />
                            <span className="input-type-title">Image</span>
                            <span className="input-type-formats">PNG, JPG, JPEG, GIF</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="inputType"
                            value="pdf"
                            checked={newProofRequirement.inputType === 'pdf'}
                            onChange={(e) => updateNewProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.FILE_PDF} className="input-type-icon" />
                            <span className="input-type-title">PDF</span>
                            <span className="input-type-formats">PDF documents</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="inputType"
                            value="link"
                            checked={newProofRequirement.inputType === 'link'}
                            onChange={(e) => updateNewProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.LINK} className="input-type-icon" />
                            <span className="input-type-title">Link</span>
                            <span className="input-type-formats">URL/Web link</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="inputType"
                            value="excel"
                            checked={newProofRequirement.inputType === 'excel'}
                            onChange={(e) => updateNewProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.FILE_EXCEL} className="input-type-icon" />
                            <span className="input-type-title">Excel</span>
                            <span className="input-type-formats">CSV, XLS, XLSX files</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Settings Section */}
                  <div className="form-section">
                    <h4 className="settings">
                      <FontAwesomeIcon icon={ICONS.SHIELD} />
                      Settings
                    </h4>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newProofRequirement.required}
                          onChange={(e) => updateNewProofRequirement('required', e.target.checked)}
                        />
                        <FontAwesomeIcon icon={ICONS.CHECK_CIRCLE} className="checkbox-icon" />
                        <span className="checkbox-text">Required proof</span>
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-actions">
              <button 
                className="create-btn"
                onClick={handleSaveProofRequirement}
                disabled={!newProofRequirement.label.en}
              >
                <FontAwesomeIcon icon={ICONS.CHECK} />
                {editingProofIndex !== null ? 'Update' : 'Add'} Proof Requirement
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowProofModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Side Quest Proof Requirement Modal */}
      {showSideQuestProofModal && (
        <div className="modal-overlay">
          <div className="modal proof-requirement-modal large-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={ICONS.PLUS} />
                {editingSideQuestProofIndex !== null ? 'Edit Side Quest Proof Requirement' : 'Add Side Quest Proof Requirement'}
              </h3>
              <button 
                className="close-btn"
                onClick={() => setShowSideQuestProofModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <form className="create-form">
                <div className="form-grid">
                  {/* Proof Label Section */}
                  <div className="form-section">
                    <h4>
                      <FontAwesomeIcon icon={ICONS.TAG} />
                      Proof Label
                    </h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newSideQuestProofRequirement.label, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newSideQuestProofRequirement.label)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <input
                        type="text"
                        value={newSideQuestProofRequirement.label[selectedLanguage]}
                        onChange={(e) => updateNewSideQuestProofRequirement('label', e.target.value, selectedLanguage)}
                        placeholder={`Enter label in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                      />
                    </div>
                  </div>

                  {/* Description Section */}
                  <div className="form-section">
                    <h4 className="description">
                      <FontAwesomeIcon icon={ICONS.INFO_CIRCLE} />
                      Description
                    </h4>
                    <div className="language-header">
                      <span></span>
                      <div className="language-controls">
                        <select 
                          className="language-select"
                          value={selectedLanguage}
                          onChange={(e) => setSelectedLanguage(e.target.value)}
                        >
                          {languages.map(lang => (
                            <option key={lang.code} value={lang.code}>
                              {isLanguageCompleted(newSideQuestProofRequirement.description, lang.code) ? '✓ ' : ''}{lang.name}
                            </option>
                          ))}
                        </select>
                        <span className="translation-counter">
                          {getCompletedTranslations(newSideQuestProofRequirement.description)}/{languages.length}
                        </span>
                      </div>
                    </div>
                    <div className="form-group">
                      <textarea
                        value={newSideQuestProofRequirement.description[selectedLanguage]}
                        onChange={(e) => updateNewSideQuestProofRequirement('description', e.target.value, selectedLanguage)}
                        placeholder={`Enter description in ${languages.find(l => l.code === selectedLanguage)?.name}`}
                        rows="3"
                      />
                    </div>
                  </div>

                  {/* Input Type Section */}
                  <div className="form-section">
                    <h4 className="settings">
                      <FontAwesomeIcon icon={ICONS.FILE_ALT} />
                      Input Type
                    </h4>
                    <div className="input-type-grid">
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="sideQuestInputType"
                            value="image"
                            checked={newSideQuestProofRequirement.inputType === 'image'}
                            onChange={(e) => updateNewSideQuestProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.IMAGE} className="input-type-icon" />
                            <span className="input-type-title">Image</span>
                            <span className="input-type-formats">PNG, JPG, JPEG, GIF</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="sideQuestInputType"
                            value="pdf"
                            checked={newSideQuestProofRequirement.inputType === 'pdf'}
                            onChange={(e) => updateNewSideQuestProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.FILE_PDF} className="input-type-icon" />
                            <span className="input-type-title">PDF</span>
                            <span className="input-type-formats">PDF documents</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="sideQuestInputType"
                            value="link"
                            checked={newSideQuestProofRequirement.inputType === 'link'}
                            onChange={(e) => updateNewSideQuestProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.LINK} className="input-type-icon" />
                            <span className="input-type-title">Link</span>
                            <span className="input-type-formats">URL/Web link</span>
                          </div>
                        </label>
                      </div>
                      
                      <div className="input-type-option">
                        <label className="input-type-label">
                          <input
                            type="radio"
                            name="sideQuestInputType"
                            value="excel"
                            checked={newSideQuestProofRequirement.inputType === 'excel'}
                            onChange={(e) => updateNewSideQuestProofRequirement('inputType', e.target.value)}
                          />
                          <div className="input-type-card">
                            <FontAwesomeIcon icon={ICONS.FILE_EXCEL} className="input-type-icon" />
                            <span className="input-type-title">Excel</span>
                            <span className="input-type-formats">CSV, XLS, XLSX files</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Settings Section */}
                  <div className="form-section">
                    <h4 className="settings">
                      <FontAwesomeIcon icon={ICONS.SHIELD} />
                      Settings
                    </h4>
                    <div className="form-group">
                      <label className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={newSideQuestProofRequirement.required}
                          onChange={(e) => updateNewSideQuestProofRequirement('required', e.target.checked)}
                        />
                        <FontAwesomeIcon icon={ICONS.CHECK_CIRCLE} className="checkbox-icon" />
                        <span className="checkbox-text">Required proof</span>
                      </label>
                    </div>
                  </div>
                </div>
              </form>
            </div>
            <div className="modal-actions">
              <button 
                className="create-btn"
                onClick={handleSaveSideQuestProofRequirement}
                disabled={!newSideQuestProofRequirement.label.en}
              >
                <FontAwesomeIcon icon={ICONS.CHECK} />
                {editingSideQuestProofIndex !== null ? 'Update' : 'Add'} Proof Requirement
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowSideQuestProofModal(false)}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal delete-modal">
            <div className="modal-header">
              <h3>
                <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE} />
                Delete Quest
              </h3>
              <button 
                className="close-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
              </button>
            </div>
            <div className="modal-content">
              <div className="delete-warning">
                <div className="warning-icon">
                  <FontAwesomeIcon icon={ICONS.EXCLAMATION_TRIANGLE} />
                </div>
                <div className="warning-content">
                  <h4>Are you sure you want to delete this quest?</h4>
                  <p>
                    <strong>"{typeof selectedQuest?.title === 'string' ? selectedQuest.title : selectedQuest?.title?.en || 'Untitled'}"</strong>
                  </p>
                  <div className="warning-details">
                    <p>This action will permanently delete:</p>
                    <ul>
                      <li>The quest and all its data</li>
                      <li>Any associated quest icon</li>
                      <li>All side quests and proof requirements</li>
                    </ul>
                    <p className="warning-note">
                      <strong>This action cannot be undone!</strong>
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                className="delete-btn"
                onClick={handleDeleteQuest}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <FontAwesomeIcon icon={ICONS.SPINNER} spin />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={ICONS.TRASH} />
                    Delete Quest
                  </>
                )}
              </button>
              <button 
                className="cancel-btn"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
              >
                <FontAwesomeIcon icon={ICONS.TIMES} />
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPageLayout>
  );
} 